

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { Space, SpaceCategory } from "@the-real-insight/tri-model";
import { useAdministrationAPI } from "../AdministrationAPI";
import EntityPhotoListEditor, {
    EntityPhoto,
} from "../components/EntityPhotoListEditor";
import { useModalContext } from "../components/ModalProvider";
import { EntityTypePill } from "../entities/EntityHierarchyBar";
import SpaceEquipmentPanel from "./SpaceEquipmentPanel";
import EntityDocumentsPanel from "../components/EntityDocumentsPanel";

import { cn } from "@/lib/utils";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";

/** Closed value set for the context part — mirrors the server's list (Daten §1.2). */
const PERSON_EXPOSURES = [
  "publikumsbereich",
  "arbeitsplatzbereich",
  "technikbereich",
  "aussenbereich",
] as const;

interface SearchSelectOption {
  key: string;
  text: string;
}

/**
 * Durchsuchbare Auswahl (früher Fluent-`ComboBox`): Popover + Command, weil die
 * Liste im Bestand eine Tippsuche hatte. Die Breite bleibt der Aufruferwert,
 * `max-w-full` hält sie auf schmalen Viewports im Container.
 */
function SearchSelect({
  id,
  label,
  placeholder,
  options,
  selectedKey,
  width,
  onSelect,
}: {
  id: string;
  label: string;
  placeholder?: string;
  options: SearchSelectOption[];
  selectedKey?: string | null;
  width?: number;
  onSelect: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.key === selectedKey);

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          id={id}
          role="combobox"
          aria-expanded={open}
          className="flex h-9 max-w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11"
          style={{ width: width ?? 280 }}
        >
          <span className={cn("line-clamp-1 text-left", !selected && "text-muted-foreground")}>
            {selected?.text ?? placeholder ?? "—"}
          </span>
          <ChevronDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={placeholder ?? "Search…"} />
            <CommandList>
              <CommandEmpty>No options</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.key}
                    value={`${option.text} ${option.key}`}
                    onSelect={() => {
                      onSelect(option.key);
                      setOpen(false);
                    }}
                  >
                    <Check
                      aria-hidden="true"
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        option.key === selectedKey ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1 whitespace-normal">{option.text}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface Properties {
  space: any;
}

export default function SpaceProperties(properties: Properties) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [tab, setTab] = useState(searchParams.get("tab") || "general");
  const { pushError } = useModalContext() as any;
  const { updateSpace } = useAdministrationAPI();
  const [space, setSpace] = useState(properties.space);
  const spaceRef = useRef(properties.space);
  const shortNameId = useId();
  const longNameId = useId();
  const categoryId = useId();
  const personExposureId = useId();

  useEffect(() => {
    spaceRef.current = space;
  }, [space]);

  useEffect(() => {
    setSpace(properties.space);
    spaceRef.current = properties.space;
  }, [properties.space]);

  useEffect(() => {
    const tParam = searchParams.get("tab");
    if (tParam === "general" || tParam === "equipment") {
      setTab(tParam);
    }
  }, [searchParams]);

  const setParams = (value: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", value);
    window.history.replaceState(null, "", url.toString());
    setTab(value);
  };

  const persistPhotos = async (photos: EntityPhoto[]) => {
    try {
      const merged = { ...spaceRef.current, photos };
      const next = new Space(merged);
      await updateSpace(next);
      setSpace(merged);
    } catch (e) {
      pushError(e);
    }
  };

  const saveSpaceFields = async (patch: Record<string, unknown>) => {
    try {
      const merged = { ...spaceRef.current, ...patch };
      const next = new Space(merged);
      await updateSpace(next);
      setSpace(merged);
    } catch (e) {
      pushError(e);
    }
  };

  return (
    <TooltipProvider>
      <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
        {/* Gebäude und Etage stehen im Pfad über der Seite (EntityHierarchyBar). */}
        <div className="min-w-0">
          <EntityTypePill>{t("entityPage.types.Space")}</EntityTypePill>
          <h2 className="mt-0.5 break-words text-xl font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))] sm:text-2xl">
            {space?.name || "—"}
          </h2>
        </div>

        <Tabs value={String(tab)} onValueChange={setParams} className="min-w-0">
          <TabsList className="max-sm:[&_[role=tab]]:min-h-[44px]">
            <TabsTrigger value="general">{t("buildingProperties.general")}</TabsTrigger>
            <TabsTrigger value="equipment">{t("ui.devicesAndEquipment")}</TabsTrigger>
            <TabsTrigger value="documents">{t("entityDocuments.tab")}</TabsTrigger>
          </TabsList>
          <TabsContent value="general" className="mt-4">
            <div className="flex min-w-0 flex-col gap-4">
              <div className="flex min-w-0 flex-wrap items-end gap-4">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <Label htmlFor={shortNameId}>{t("spaceProperties.shortName")}</Label>
                  <Input
                    id={shortNameId}
                    className="w-[280px] max-w-full"
                    value={space?.name ?? ""}
                    onChange={(event) => {
                      const name = event.target.value;
                      setSpace((s: any) => {
                        const next = { ...s, name: name ?? "" };
                        spaceRef.current = next;
                        return next;
                      });
                    }}
                    onBlur={() =>
                      void saveSpaceFields({
                        name: spaceRef.current?.name ?? "",
                      })
                    }
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <Label htmlFor={longNameId}>{t("spaceProperties.longName")}</Label>
                  <Input
                    id={longNameId}
                    className="w-[280px] max-w-full"
                    value={space?.longName ?? ""}
                    onChange={(event) => {
                      const longName = event.target.value;
                      setSpace((s: any) => {
                        const next = { ...s, longName: longName ?? "" };
                        spaceRef.current = next;
                        return next;
                      });
                    }}
                    onBlur={() =>
                      void saveSpaceFields({
                        longName: spaceRef.current?.longName ?? "",
                      })
                    }
                  />
                </div>
                <SearchSelect
                  id={categoryId}
                  label={t("spaceProperties.type")}
                  placeholder={t("ui.pleaseSelect")}
                  options={Object.keys(SpaceCategory)
                    .filter((item) => isNaN(Number(item)))
                    .map((key: string) => ({ key, text: key }))}
                  selectedKey={space?.category}
                  onSelect={(key) => {
                    void saveSpaceFields({ category: key });
                  }}
                />
                {/* Context part: are people present here at all? Held on the
                    space because every asset in the room shares the value. The
                    options carry an explanatory line — the four terms overlap
                    otherwise. A missing value stays missing, no default.

                    Breite wie im Bestand auf den längsten Eintrag ausgelegt
                    ("Arbeitsplatzbereich — regelmäßiger Aufenthalt von
                    Beschäftigten"); der Wert samt Erklärung muss hineinpassen. */}
                <SearchSelect
                  id={personExposureId}
                  label={t("context.personExposure")}
                  placeholder={t("context.notRecorded")}
                  width={520}
                  options={PERSON_EXPOSURES.map((value) => ({
                    key: value,
                    text: `${t(`context.personExposureValue.${value}`)} — ${t(
                      `context.personExposureHint.${value}`,
                    )}`,
                  }))}
                  selectedKey={space?.personExposure ?? null}
                  onSelect={(key) => {
                    void saveSpaceFields({ personExposure: key });
                  }}
                />
              </div>
              <EntityPhotoListEditor
                photos={space?.photos}
                onPhotosChange={persistPhotos}
              />
            </div>
          </TabsContent>
          <TabsContent value="equipment" className="mt-4">
            <SpaceEquipmentPanel space={space} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <EntityDocumentsPanel
              entityType="Space"
              entity={space}
              onChange={(updated: any) => {
                spaceRef.current = updated;
                setSpace(updated);
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}
